/**
 * Reglas puras del programa de fidelización (sin Firestore, para poder testear).
 * Las escrituras viven en fidelizacionHelper.js, igual que consumo.js ↔ inventarioHelper.js
 */
import { formatDate } from './format.js';

/**
 * Normaliza el teléfono que se usa como ID del cliente.
 * El mismo número escrito "+56 9 1234 5678" o "56912345678" tiene que caer en
 * el mismo documento, o el cliente aparece dos veces y nunca "vuelve".
 */
export const normalizarTel = (tel) => String(tel ?? '').replace(/[\s()+-]/g, '');

/**
 * Puntos que gana una venta, proporcional al monto y redondeado hacia abajo
 * (una venta de $19.990 con 10 pts/mil da 199, no 190). Es la fórmula que el
 * POS viene usando: cambiarla alteraría los puntos que ya acumularon.
 *
 * @param {number} total - Total cobrado (con IVA, sin propina)
 * @param {number} puntosPorMil - Puntos por cada $1.000
 */
export const puntosPorVenta = (total, puntosPorMil = 10) =>
  Math.max(0, Math.floor((Number(total) || 0) / 1000 * (Number(puntosPorMil) || 0)));

/**
 * Fecha de última visita, venga como Timestamp de Firestore, Date o string.
 * Usa formatDate (dd/MM/yyyy fijo) en vez de toLocaleDateString, que en es-CL
 * no da el mismo separador en el navegador que en Node.
 */
export const fmtUltimaVisita = (v) => {
  const d = v?.toDate?.() || (v ? new Date(v) : null);
  if (!d || isNaN(d.getTime())) return '—';
  return formatDate(d);
};

// Sin tildes ni mayúsculas: "josé" encuentra a "Jose"
const plano = (t = '') => String(t).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Clientes que coinciden con lo escrito en el POS: con letras busca por
 * nombre; con números, por teléfono parcial (desde 3 dígitos). Los más
 * frecuentes primero.
 */
export const filtrarClientes = (clientes = [], texto = '', max = 6) => {
  const q = String(texto).trim();
  if (q.length < 2) return [];
  const porTelefono = /^[\d\s()+-]+$/.test(q);
  const digitos = q.replace(/\D/g, '');
  if (porTelefono && digitos.length < 3) return [];
  return clientes
    .filter(c => porTelefono
      ? String(c.telefono || c.id || '').includes(digitos)
      : plano(c.nombre).includes(plano(q)))
    .sort((a, b) => (b.totalVisitas || 0) - (a.totalVisitas || 0))
    .slice(0, max);
};

/**
 * Días que faltan para el cumpleaños ("MM-DD"): 0 = hoy. null si no hay fecha.
 * Cruza el año (el 31-12 ve el cumpleaños del 02-01 a 2 días) y el 29 de
 * febrero se celebra el 28 en los años no bisiestos.
 */
export const diasParaCumple = (mmdd, hoy = new Date()) => {
  const [mes, dia] = String(mmdd || '').split('-').map(Number);
  if (!mes || !dia) return null;
  const base = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  const enAnio = (anio) => {
    const bisiesto = new Date(anio, 1, 29).getMonth() === 1;
    return new Date(anio, mes - 1, mes === 2 && dia === 29 && !bisiesto ? 28 : dia);
  };
  let prox = enAnio(base.getFullYear());
  if (prox < base) prox = enAnio(base.getFullYear() + 1);
  return Math.round((prox - base) / 86400000);
};

/** Aviso para el cajero si el cumpleaños es hoy o dentro de la semana; '' si no. */
export const textoCumple = (mmdd, hoy = new Date()) => {
  const n = diasParaCumple(mmdd, hoy);
  if (n === null || n > 7) return '';
  return n === 0 ? '🎂 ¡Hoy está de cumpleaños!' : n === 1 ? '🎂 Mañana está de cumpleaños' : `🎂 Cumple en ${n} días`;
};
